import React, { useState } from 'react';
import './Projects.css';

const publicAssetSrc = (path) => `${process.env.PUBLIC_URL || ''}${encodeURI(path)}`;

/** Synced with `public/Testing/Finished Projects` (copies to `build/Testing/Finished Projects` on build). */
const FINISHED_PROJECTS_BASE = '/Testing/Finished Projects';
const fp = (filename) => `${FINISHED_PROJECTS_BASE}/${filename}`;
const UNFINISHED_PLACEHOLDER_IMAGE = '/Testing/test-placeholder.svg';

// Modal component for displaying full-size images
const ProjectModal = ({ project, onClose }) => {
  if (!project) return null;
  
  return (
    <div className="project-modal-overlay" onClick={onClose}>
      <div className="project-modal-content">
        <button className="modal-close-btn" onClick={onClose}>×</button>
        <div className="modal-image-container">
          <img src={publicAssetSrc(project.image)} alt={project.title} />
          <div className="modal-project-title">
            <h2>{project.title}</h2>
            <p>{project.location}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Projects = ({ setCurrentPage, setActiveSection }) => {
  const [activeCategory, setActiveCategory] = useState('finished');
  const [selectedProject, setSelectedProject] = useState(null);

  const projectCategories = [
    { id: 'finished', name: 'FINISHED PROJECTS' },
    { id: 'unfinished', name: 'UNFINISHED PROJECTS' }
  ];

  /** Placeholder rows until real ongoing projects are listed again. */
  const unfinishedProjects = [
    {
      id: 1,
      title: 'Ongoing project — details soon',
      category: 'unfinished',
      image: UNFINISHED_PLACEHOLDER_IMAGE,
      location: 'Philippines',
      isPlaceholder: true
    },
    {
      id: 2,
      title: 'Ongoing project — details soon',
      category: 'unfinished',
      image: UNFINISHED_PLACEHOLDER_IMAGE,
      location: 'Philippines',
      isPlaceholder: true
    },
    {
      id: 3,
      title: 'Ongoing project — details soon',
      category: 'unfinished',
      image: UNFINISHED_PLACEHOLDER_IMAGE,
      location: 'Philippines',
      isPlaceholder: true
    }
  ];

  const unfinishedFilenames = new Set();

  /** Full set under `public/Testing/Finished Projects` (nothing withheld — unfinished tab uses placeholders only). */
  const finishedProjectSpecs = [
    { file: 'AGAPEYA.jpg', title: 'Agapeya Development', location: 'Metro Manila' },
    { file: 'AQUA TOUR.jpg', title: 'Aqua Tour', location: 'Lipa, Batangas' },
    { file: 'ATENEO.jpg', title: 'Ateneo de Manila University', location: 'Quezon City' },
    { file: 'BAYSHORE.jpg', title: 'Bayshore Residential Resort', location: 'Pasay City' },
    { file: 'BSP TANAUAN.jpg', title: 'BSP Tanauan', location: 'Tanauan, Batangas' },
    { file: 'C5 EXT.jpg', title: 'C5 Extension', location: 'Metro Manila' },
    { file: 'CAGAYAN DE ORO.png', title: 'Cagayan de Oro', location: 'Cagayan de Oro' },
    { file: 'CALAX.jpg', title: 'CALAX Project', location: 'Laguna' },
    { file: 'CALM RESIDENCE.jpg', title: 'CALM Residences', location: 'Metro Manila' },
    { file: 'DC CALAMBA.jpg', title: 'S&R / DC Calamba', location: 'Calamba, Laguna' },
    { file: 'EASTWOOD.jpg', title: 'Eastwood', location: 'Quezon City' },
    { file: 'FPDI WAREHOUSE.jpg', title: 'FPDI Development', location: 'Metro Manila' },
    { file: 'FS PHIL BATAAN.jpg', title: 'FS Phil Bataan', location: 'Bataan' },
    { file: 'FSPHIL.jpg', title: 'FS Phil', location: 'Laguna' },
    { file: 'KING SQUARE.jpg', title: 'King Square', location: 'Metro Manila' },
    { file: 'LRT 1 EXTENSION.jpg', title: 'Lotte Gulermak EEI Joint Venture — LRT', location: 'Metro Manila' },
    { file: 'MCDONALD PANGASINAN.jpg', title: "McDonald's Pangasinan", location: 'Pangasinan' },
    { file: 'NORTH RAILWAY.jpg', title: 'North and South Railway Project', location: 'Meycauyan, Bulacan' },
    { file: 'PARKLINKS TOWER.jpg', title: 'Parklinks Tower', location: 'Metro Manila' },
    { file: 'PUREGOLD TANZA.jpg', title: 'Puregold Tanza', location: 'Tanza, Cavite' },
    { file: 'RIO TUBA.jpg', title: 'Rio Tuba Project', location: 'Palawan' },
    { file: 'SCRP PROJ.jpg', title: 'SCRP Project', location: 'Philippines' },
    { file: 'SKYWAY.jpg', title: 'Skyway Project', location: 'Metro Manila' },
    { file: 'SM CALOOCAN.jpg', title: 'SM Development — SM Caloocan', location: 'Caloocan' },
    { file: 'SMDC JOY.jpg', title: 'SMDC Joy Residence', location: 'Metro Manila' },
    { file: 'UNILIVER.jpg', title: 'Unilever', location: 'Philippines' },
    { file: 'ZADIA TOWER.jpg', title: 'Zadia Residential Development', location: 'Metro Manila' }
  ];

  const finishedProjects = finishedProjectSpecs
    .filter((spec) => !unfinishedFilenames.has(spec.file))
    .map((spec, index) => ({
      id: 100 + index,
      title: spec.title,
      category: 'finished',
      image: fp(spec.file),
      location: spec.location
    }));

  const projects = [...finishedProjects, ...unfinishedProjects];

  const filteredProjects = projects.filter(
    (project) => project.category === activeCategory
  );

  const goToHomeFromLogo = () => {
    setCurrentPage('home');
    setActiveSection('home');
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    document.querySelector('.mobile-menu')?.classList.remove('active');
  };

  return (
    <div className="projects-page">
      {/* Navigation Bar */}
      <div className="icon-navbar visible">
        <div className="button-container">
          {/* GGC Logo */}
          <button
            type="button"
            className="nav-logo"
            onClick={goToHomeFromLogo}
            aria-label="Go to home"
            title="Home"
          >
            <img 
              src={`${process.env.PUBLIC_URL}/geologo.png`} 
              alt="" 
              className="nav-logo-img"
            />
          </button>
          
          <button 
            className="button"
            onClick={() => {
              setCurrentPage('home');
              setActiveSection('home');
            }}
            title="Home"
          >
            Home
          </button>
          
          <button 
            className="button"
            onClick={() => {
              setCurrentPage('services');
              setActiveSection('services');
            }}
            title="Services"
          >
            Services
          </button>

          <button 
            className="button active"
            onClick={() => {
              setCurrentPage('projects');
              setActiveSection('projects');
            }}
            title="Projects"
          >
            Projects
          </button>

                    <button
            className="button"
            onClick={(e) => {
              e.preventDefault();
              // Go to home page first
              setCurrentPage('home');
              setActiveSection('branches');
              
              // Use a more reliable method to wait for the element
              const waitForBranchesSection = () => {
                const branchesSection = document.getElementById('branches');
                if (branchesSection) {
                  branchesSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  // If not found, try again in 50ms
                  setTimeout(waitForBranchesSection, 50);
                }
              };
              
              // Start waiting for the section after a short delay
              setTimeout(waitForBranchesSection, 150);
            }}
            title="Branches"
          >
            Branches
          </button>

          <button 
            className="button"
            onClick={(e) => {
              e.preventDefault();
              // Go to testing page first
              setCurrentPage('testing');
              setActiveSection('testing');
              
              // Use a more reliable method to wait for the element
              const waitForTestingSection = () => {
                const testingSection = document.getElementById('testing');
                if (testingSection) {
                  testingSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  // If not found, try again in 50ms
                  setTimeout(waitForTestingSection, 50);
                }
              };
              
              // Start waiting for the section after a short delay
              setTimeout(waitForTestingSection, 150);
            }}
            title="Testing"
          >
            Testing
          </button>

          <button
            className="button"
            onClick={() => {
              setCurrentPage('about');
              setActiveSection('about');
              window.scrollTo(0, 0);
            }}
            title="About"
          >
            About
          </button>
        </div>

        {/* Mobile Top Navigation */}
        <div className="mobile-nav-container">
          <button
            type="button"
            className="mobile-logo"
            onClick={goToHomeFromLogo}
            aria-label="Go to home"
            title="Home"
          >
            GEOMATIXS
          </button>
          <button 
            className="mobile-menu-toggle"
            onClick={() => {
              const menu = document.querySelector('.mobile-menu');
              if (menu) {
                menu.classList.toggle('active');
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        <div className="mobile-menu">
          <button 
            className="mobile-menu-item"
            onClick={() => {
              setCurrentPage('home');
              setActiveSection('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            <span>Home</span>
          </button>
          <button 
            className="mobile-menu-item"
            onClick={() => {
              setCurrentPage('services');
              setActiveSection('services');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span>Services</span>
          </button>
          <button 
            className="mobile-menu-item active"
            onClick={() => {
              setCurrentPage('projects');
              setActiveSection('projects');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
            <span>Projects</span>
          </button>
          <button 
            className="mobile-menu-item"
            onClick={(e) => {
              e.preventDefault();
              if (setCurrentPage !== 'home') {
                setCurrentPage('home');
                setTimeout(() => {
                  setActiveSection('branches');
                  const branchesSection = document.getElementById('branches');
                  if (branchesSection) {
                    branchesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              } else {
                setActiveSection('branches');
                const branchesSection = document.getElementById('branches');
                if (branchesSection) {
                  branchesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>Branches</span>
          </button>
          <button 
            className="mobile-menu-item"
            onClick={() => {
              setCurrentPage('testing');
              setActiveSection('testing');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V1h-2v1H7V1H5v1H4.5C3.67 2 3 2.67 3 3.5v15C3 19.33 3.67 20 4.5 20h15c.83 0 1.5-.67 1.5-1.5v-15C21 2.67 20.33 2 19.5 2z"/>
            </svg>
            <span>Testing</span>
          </button>
          <button
            className="mobile-menu-item"
            onClick={() => {
              setCurrentPage('about');
              setActiveSection('about');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <span>About</span>
          </button>
        </div>
      </div>

      <div className="container">
        <div className="projects-header">
          <h1>Our Project Gallery</h1>
          <p>Showcasing our expertise across various construction and development projects</p>
        </div>
        
        <div className="project-categories">
          <div className="category-tabs">
            {projectCategories.map(category => (
              <button
                key={category.id}
                className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
        
        <div className="projects-grid">
          {filteredProjects.map(project => (
            <div
              key={project.id}
              className={`project-card${project.isPlaceholder ? ' project-card--placeholder' : ''}`}
              onClick={() => {
                if (!project.isPlaceholder) setSelectedProject(project);
              }}
            >
              <div className="project-image">
                <img 
                  src={publicAssetSrc(project.image)} 
                  alt={project.title}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />
                <div className="image-fallback" style={{display: 'none'}}>
                  <span>🏗️</span>
                </div>
              </div>
              <div className="project-info">
                <h3>{project.title}</h3>
                <div className="project-meta">
                  <span className="location">📍 {project.location}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <ProjectModal 
          project={selectedProject} 
          onClose={() => setSelectedProject(null)} 
        />
      </div>
    </div>
  );
};

export default Projects;
